import React from 'react'
import {
  Box,
  Divider,
  FormControlLabel,
  FormControlLabelProps,
  Radio,
  RadioGroup,
} from '@mui/material'

import { exportOptions, pretestOptions } from '../main-types'
import {
  featureFlagLabels,
  filenameConventionOptions,
  FilenameConventionRadioGroupProps,
} from './use-feature-flags-props'

export type GetCheckboxProps = (label: string, field: string) => FormControlLabelProps

const FilenameConventionSection = ({
  getFilenameConventionProps,
}: {
  getFilenameConventionProps: () => FilenameConventionRadioGroupProps
}) => {
  const { value, disabled, ...radioGroupProps } = getFilenameConventionProps()
  return (
    <>
      <Divider sx={{ mt: 2 }}>Export Download Filename</Divider>
      <RadioGroup row value={value} {...radioGroupProps}>
        {filenameConventionOptions.map((option) => (
          <FormControlLabel
            key={option.value}
            value={option.value}
            control={<Radio size="small" color="secondary" />}
            disabled={disabled}
            slotProps={{ typography: { fontSize: 14 } }}
            label={option.label}
          />
        ))}
      </RadioGroup>
      <Box typography="caption" color="text.secondary">
        Example: {filenameConventionOptions.find((o) => o.value === (value || ''))?.example}
      </Box>
    </>
  )
}

type FeatureFlagsSectionProps = {
  getCheckboxProps: GetCheckboxProps
  getFilenameConventionProps?: () => FilenameConventionRadioGroupProps
}

export const FeatureFlagsSection = ({
  getCheckboxProps,
  getFilenameConventionProps,
}: FeatureFlagsSectionProps) => {
  return (
    <>
      <Divider sx={{ mt: 2 }}>Flags</Divider>
      <FormControlLabel {...getCheckboxProps(featureFlagLabels.learn_v2, 'features.learn_v2')} />
      <FormControlLabel
        {...getCheckboxProps(featureFlagLabels.has_option_rationale, 'hasOptionRationale')}
      />
      <FormControlLabel
        {...getCheckboxProps(featureFlagLabels.possible_keys, 'features.possible_keys')}
      />
      <FormControlLabel
        {...getCheckboxProps(featureFlagLabels.rationales, 'features.rationales')}
      />
      <FormControlLabel
        {...getCheckboxProps(
          featureFlagLabels.probuilder_integration,
          'features.probuilder_integration',
        )}
      />
      <FormControlLabel
        {...getCheckboxProps(featureFlagLabels.item_types_v2, 'features.item_types_v2')}
      />
      <FormControlLabel
        {...getCheckboxProps(featureFlagLabels.model_type_filter, 'features.model_type_filter')}
      />
      <FormControlLabel
        {...getCheckboxProps(featureFlagLabels.webhook_integration, 'features.webhook_integration')}
      />

      <Divider sx={{ mt: 2 }}>Pre-test Flags</Divider>
      <Box display="flex" justifyContent="space-between" my={1}>
        {pretestOptions.map((option) => (
          <FormControlLabel
            {...getCheckboxProps(featureFlagLabels[option.value], `features.${option.value}`)}
            key={option.value}
          />
        ))}
      </Box>

      <Divider sx={{ mt: 2 }}>Export Options</Divider>
      <Box display="flex" justifyContent="space-between" my={1}>
        {exportOptions.map((format) => (
          <FormControlLabel
            {...getCheckboxProps(format, `features.export_file.${format}`)}
            key={format}
          />
        ))}
      </Box>
      <FormControlLabel
        {...getCheckboxProps(
          featureFlagLabels.use_identifier_for_exports,
          'features.use_identifier_for_exports',
        )}
      />
      <FormControlLabel
        {...getCheckboxProps(
          featureFlagLabels.images_zip_file_export,
          'features.images_zip_file_export',
        )}
      />
      <Box typography="caption">
        When enabled, all exports will be in zip format, regardless of image presence.
      </Box>

      {getFilenameConventionProps && (
        <FilenameConventionSection getFilenameConventionProps={getFilenameConventionProps} />
      )}

      <Divider sx={{ mt: 2 }}>Experimental Features</Divider>
      <FormControlLabel
        {...getCheckboxProps(featureFlagLabels.generate_math, 'features.generate_math')}
      />
    </>
  )
}
